import { useState, useEffect, useCallback } from 'react';

const slides = [
  {
    id: 1,
    tag: '교육 현장',
    title: '체계적인 실습 중심 교육',
    desc: '전문 강사진의 1:1 지도 아래 실제 현장과 동일한 환경에서 실습합니다.',
    image: 'https://public.readdy.ai/ai/img_res/242b0e5228762e273de101f89f82718a.jpg',
  },
  {
    id: 2,
    tag: '자격증 수여식',
    title: '본회 공식 자격증 수여',
    desc: '교육 수료 후 (사)대한문신사중앙회 공식 자격증을 취득하여 전문가로 인정받으세요.',
    image: 'https://readdy.ai/api/search-image?query=bright%20formal%20Korean%20professional%20certification%20ceremony%2C%20graduates%20receiving%20official%20certificates%20from%20association%20director%2C%20formal%20event%20in%20bright%20conference%20hall%2C%20professional%20attire%2C%20celebratory%20atmosphere%2C%20gold%20and%20navy%20color%20scheme&width=1200&height=700&seq=slide_v4_2&orientation=landscape',
  },
  {
    id: 3,
    tag: '본회 공식 행사',
    title: '전국 대한문신사중앙회 네트워크와 함께',
    desc: '전국 지부 회원들과 함께하는 세미나, 컨벤션, 기술 교류 행사에 참여하세요.',
    image: 'https://readdy.ai/api/search-image?query=bright%20professional%20Korean%20beauty%20industry%20conference%20seminar%20with%20many%20attendees%2C%20large%20bright%20convention%20hall%2C%20professional%20speakers%20on%20stage%2C%20networking%20event%2C%20official%20association%20gathering%2C%20navy%20and%20gold%20decorations%2C%20prestigious%20atmosphere&width=1200&height=700&seq=slide_v4_3&orientation=landscape',
  },
  {
    id: 4,
    tag: '위생교육',
    title: '필수 위생교육 원스톱 지원',
    desc: '문신사 영업에 필요한 위생교육 이수부터 이수증 발급까지 지부에서 한번에 해결하세요.',
    image: 'https://readdy.ai/api/search-image?query=bright%20professional%20hygiene%20education%20class%20for%20Korean%20beauty%20professionals%2C%20clean%20modern%20classroom%20with%20instructor%2C%20students%20taking%20notes%2C%20official%20educational%20setting%2C%20bright%20natural%20lighting%2C%20professional%20and%20trustworthy%20atmosphere&width=1200&height=700&seq=slide_v4_4&orientation=landscape',
  },
];

export default function ActivitySlide() {
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const goTo = useCallback((idx: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrent(idx);
    setTimeout(() => setIsAnimating(false), 500);
  }, [isAnimating]);

  const prev = useCallback(() => goTo((current - 1 + slides.length) % slides.length), [current, goTo]);
  const next = useCallback(() => goTo((current + 1) % slides.length), [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  return (
    <section className="py-16 md:py-20 bg-[#F4F6FA]">
      <div className="max-w-7xl mx-auto px-5 md:px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 md:mb-10 gap-4">
          <div className="text-center md:text-left">
            <span className="inline-block text-xs font-bold tracking-widest text-[#C9A84C] uppercase border border-[#C9A84C]/40 rounded-sm px-3 py-1 mb-4">본회 공식 활동</span>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-black text-[#1B3A6B]">대한문신사중앙회 중구지부 활동 현장</h2>
          </div>
          <div className="flex items-center gap-2 justify-center md:justify-end">
            <button
              onClick={prev}
              className="w-10 h-10 flex items-center justify-center rounded-md border border-gray-200 bg-white text-[#1B3A6B] hover:bg-[#1B3A6B] hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
            </button>
            <button
              onClick={next}
              className="w-10 h-10 flex items-center justify-center rounded-md border border-gray-200 bg-white text-[#1B3A6B] hover:bg-[#1B3A6B] hover:text-white transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>

        {/* Slide */}
        <div className="relative rounded-xl overflow-hidden bg-white border border-gray-100">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* Image */}
            <div className="relative h-56 md:h-72 lg:h-96 overflow-hidden">
              <img
                key={slides[current].id}
                src={slides[current].image}
                alt={slides[current].title}
                className="w-full h-full object-cover object-top transition-opacity duration-500"
              />
              <span className="absolute top-4 left-4 bg-[#C9A84C] text-white text-xs font-bold px-3 py-1 rounded-sm">
                {slides[current].tag}
              </span>
            </div>

            {/* Content */}
            <div className="flex flex-col justify-center px-6 md:px-8 lg:px-12 py-8 md:py-10">
              <p className="text-[#C9A84C] text-xs font-bold tracking-widest uppercase mb-3">
                {String(current + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
              </p>
              <h3 className="text-xl md:text-2xl lg:text-3xl font-black text-[#1B3A6B] mb-4 leading-tight">
                {slides[current].title}
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-8">
                {slides[current].desc}
              </p>

              {/* Dots */}
              <div className="flex items-center gap-2">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => goTo(i)}
                    className={`transition-all duration-300 rounded-full cursor-pointer ${
                      i === current
                        ? 'w-8 h-2 bg-[#1B3A6B]'
                        : 'w-2 h-2 bg-gray-200 hover:bg-gray-300'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
